/**
 * Pinia distributor-summary store (PR-2 of the sidebar sub-text + status
 * dots boulder -- https://trello.com/c/cn3zEOOx).
 *
 * Hydrates the distributor sidebar's per-row sub-text + status dot data
 * from the backend ``POST /api/distributor/dashboard-summary`` endpoint
 * (api/src/endpoints/dashboard.py). The endpoint returns a single
 * 12-key ``summary`` object; the layout binds each row to its slot.
 *
 * Lifecycle:
 *   - DistributorLayout.vue mount calls ``fetch()`` (PR-3 will wire this).
 *   - Impersonation stop calls ``clear()`` from the impersonation store --
 *     true-SA has no effective tenant and the layout must show nothing.
 *   - Impersonation start triggers a page reload (see
 *     DistributorList.doImpersonate), so DistributorLayout's mount fires
 *     after the reload and a fresh fetch happens naturally. No explicit
 *     start-side hook is needed here.
 *
 * Why the same axios instance as other services:
 *   ``@/services/api`` is the shared axios instance with the bearer-token
 *   request interceptor + base-URL configuration. Calling raw
 *   ``axios.post`` would bypass both. The endpoint URL is relative
 *   (no ``/api`` prefix) because the instance's baseURL ends in ``/api``.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/services/api'
import { useEventBus } from '@/stores/eventBus'
import { effectiveTenantId } from '@/stores/effectiveTenant'
import { SIDEBAR_REFRESH_EVENT } from '@/stores/sidebarRefresh'

// The summary-store keys the Distributor sidebar owns -- the ``id`` values on
// a ``sidebar:refresh`` event that are RELEVANT to this layout. Under the
// generic delegated model (Jake, 2026-07-14) there is ONE event; the id
// decides relevance. Every one of these keys is a field in this layout's
// dashboard-summary payload (api/src/endpoints/dashboard.py builds the same
// set), so ANY of them changing means the one summary endpoint should
// coarse-refetch. Kept as the full backend key set (not just the
// currently-bound subset) so a later sidebar row binding an existing summary
// key needs no change here.
const DISTRIBUTOR_KEYS = Object.freeze(
    new Set([
        'stores',
        'decoration_library',
        // SHARED KEY: 'clients' is also a SUPERADMIN_KEYS entry
        // (stores/superAdminSummary.js). A true-SA clients mutation
        // (superadmin/ClientsList.vue) emits with tenantId:null, and this
        // store's stale-tenant guard in maybeRefetch only DROPS when
        // eventTenant != null -- so a null-tenant SA emit would NOT be filtered
        // out here. Correctness therefore rests on DistributorLayout and
        // SuperAdminLayout never co-mounting (route-exclusive shells) plus
        // teardown-on-unmount, NOT on the tenant guard. If you ever change the
        // guard or the shell mounting, re-audit this cross-shell overlap.
        'clients',
        'approvals',
        'orders',
        'performance',
        'payouts',
        'profile',
        'team',
        'payments',
        'pricing',
        'branding',
        'tax_nexus',
        'legal',
        // Setup checklist (IA unit U7). ``setup`` rides beside ``summary``
        // on the same response; ``dashboard`` / ``company`` / ``suppliers``
        // are the sidebar slots derived from it. A page whose save changes a
        // setup row may emit any of these to refresh the badge and dots.
        'setup',
        'dashboard',
        'company',
        'suppliers',
    ]),
)

export const useDistributorSummaryStore = defineStore('distributorSummary', () => {
    // ---- state ----
    const summary = ref(null)
    // The setup checklist (``setup`` beside ``summary`` in the same
    // response; api/src/services/setup_checklist.py). Null until fetched,
    // for a true SuperAdmin, and after a failed fetch -- never a stale
    // "done" from an earlier read.
    const setup = ref(null)
    const loading = ref(false)
    const error = ref(null)
    // Epoch guard: bumped on clear() so an in-flight fetch from a
    // previous tenant cannot repopulate the store after impersonation
    // stops. The current `ImpersonationBanner` caller navigates the
    // page after stop, which would also kill the request -- but the
    // store's contract should not depend on the caller's navigation
    // behavior. Future callers (e.g. a soft-stop affordance on another
    // surface) get correctness for free.
    let epoch = 0

    // ---- actions ----
    /**
     * Fetch the sidebar summary for the effective tenant.
     *
     * Always-fresh: no caching this PR (per plan section 9). On error,
     * ``summary`` resets to null so the layout shows the empty state
     * rather than stale data from a prior tenant.
     *
     * Epoch-guarded: if ``clear()`` is called while a fetch is in
     * flight, the late-arriving response is dropped on the floor -- the
     * cleared state wins.
     */
    async function fetch() {
        const myEpoch = epoch
        loading.value = true
        error.value = null
        try {
            const res = await api.post('/distributor/dashboard-summary')
            if (myEpoch !== epoch) return
            summary.value = res.data.summary
            setup.value = res.data.setup ?? null
        } catch (e) {
            if (myEpoch !== epoch) return
            error.value = e.message || 'fetch failed'
            summary.value = null
            setup.value = null
        } finally {
            if (myEpoch === epoch) loading.value = false
        }
    }

    /**
     * Reset the store to its initial empty state.
     *
     * Called when impersonation stops -- the true-SA case has no
     * effective tenant, so we wipe the prior tenant's data rather than
     * refetch. Bumps the fetch epoch so any in-flight fetch's response
     * is ignored when it arrives.
     */
    function clear() {
        epoch += 1
        summary.value = null
        setup.value = null
        error.value = null
        loading.value = false
    }

    /**
     * Event-driven refresh entry point (generic sidebar:refresh delegation).
     *
     * Applies the two relevance guards BEFORE delegating to the existing
     * epoch-guarded ``fetch()`` -- it is deliberately NOT a raw fetch:
     *
     *   (a) cap gate: ``canReadSummary()`` must be truthy. The dashboard-
     *       summary endpoint is gated on ``distributor.reporting.read``; a
     *       team member (e.g. sales_executive) triggering a mutation event
     *       must not fire a doomed 403. The layout passes its already-computed
     *       ``canReadSummary`` predicate in via ``subscribe()`` so the guard
     *       lives at the single subscribe seam (design Section 7c). Defaults
     *       open when omitted.
     *   (b) stale-tenant guard: if the event carries a ``tenantId`` that
     *       differs from the current effective tenant, drop it -- an event
     *       fired during impersonation teardown must not refetch for a tenant
     *       we are no longer showing (design Section 7b). An absent payload
     *       tenantId, or an unknown current tenant, is treated as "always
     *       relevant" (a redundant refetch is never a correctness bug).
     *
     * Epoch safety is inherited: ``fetch()`` still drops a response that
     * arrives after a ``clear()`` (distributorSummary epoch guard), so this
     * cannot repopulate a cleared store.
     */
    function maybeRefetch(payload = {}, opts = {}) {
        const canRead =
            typeof opts.canReadSummary === 'function'
                ? opts.canReadSummary()
                : true
        if (!canRead) return

        const eventTenant = payload?.tenantId
        if (eventTenant != null) {
            const current = effectiveTenantId()
            if (current != null && String(eventTenant) !== String(current)) {
                return
            }
        }

        fetch()
    }

    /**
     * Register the distributor summary's refresh handler on the event bus and
     * return the teardown fn. Called once from DistributorLayout's
     * ``onMounted``; torn down on ``onUnmounted`` so no listener survives a
     * shell switch (design Section 8).
     *
     * Delegation: subscribes to the SINGLE ``sidebar:refresh`` event and only
     * refetches when the event's ``id`` is a summary key THIS layout owns
     * (``DISTRIBUTOR_KEYS``). The id is the relevance token -- there is no
     * per-domain event to enumerate; a new distributor tab that mutates an
     * existing summary counter just emits ``sidebar:refresh`` with that key and
     * this handler already routes it, no subscribe-side change.
     *
     * @param {{ canReadSummary?: () => boolean }} [opts]
     *   ``canReadSummary`` -- the layout's cap predicate, threaded into
     *   ``maybeRefetch`` so the reporting.read gate is enforced per event.
     */
    function subscribe(opts = {}) {
        const bus = useEventBus()
        return bus.on(SIDEBAR_REFRESH_EVENT, (payload) => {
            if (!DISTRIBUTOR_KEYS.has(payload?.id)) return
            maybeRefetch(payload, opts)
        })
    }

    return {
        // state
        summary,
        setup,
        loading,
        error,
        // actions
        fetch,
        clear,
        maybeRefetch,
        subscribe,
    }
})
